import { useEffect, useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CopyButton } from "@/components/ui/copy-button";
import { Spinner } from "@/components/ui/spinner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { EditWebhookSubscriptionDialog } from "@/components/EditWebhookSubscriptionDialog";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  ChevronRightIcon,
  PencilIcon,
  PowerIcon,
  PowerOffIcon,
  RefreshCwIcon,
} from "lucide-react";

type WebhookSubscription = components["schemas"]["WebhookSubscriptionResponse"];
type WebhookDelivery = components["schemas"]["WebhookDeliveryResponse"];

interface WebhookSubscriptionsTableProps {
  clientId: string;
}

interface DeliveriesState {
  loading: boolean;
  error: string | null;
  deliveries: WebhookDelivery[];
  retrying: Record<string, boolean>;
}

export function WebhookSubscriptionsTable({
  clientId,
}: WebhookSubscriptionsTableProps) {
  const [subscriptions, setSubscriptions] = useState<WebhookSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchKey, setRefetchKey] = useState(0);

  const [openIds, setOpenIds] = useState<string[]>([]);
  const [deliveriesById, setDeliveriesById] = useState<
    Record<string, DeliveriesState>
  >({});
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<WebhookSubscription | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .GET("/admin/clients/{clientId}/webhook-subscriptions", {
        params: { path: { clientId } },
      })
      .then(({ data, error: apiError }) => {
        if (cancelled) return;
        if (apiError) {
          setError("Failed to load webhook subscriptions.");
        } else {
          setSubscriptions(data ?? []);
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, refetchKey]);

  async function loadDeliveries(subscriptionId: string) {
    setDeliveriesById((prev) => ({
      ...prev,
      [subscriptionId]: {
        loading: true,
        error: null,
        deliveries: [],
        retrying: {},
      },
    }));

    const { data, error: apiError } = await api.GET(
      "/admin/clients/{clientId}/webhook-subscriptions/{id}/deliveries",
      {
        params: { path: { clientId, id: subscriptionId }, query: { limit: 50 } },
      },
    );

    setDeliveriesById((prev) => ({
      ...prev,
      [subscriptionId]: {
        loading: false,
        error: apiError ? "Failed to load deliveries." : null,
        deliveries: data ?? [],
        retrying: {},
      },
    }));
  }

  function handleAccordionChange(values: string[]) {
    setOpenIds(values);
    for (const id of values) {
      if (!deliveriesById[id]) loadDeliveries(id);
    }
  }

  async function handleToggleActive(sub: WebhookSubscription) {
    if (!sub.id) return;
    setTogglingId(sub.id);
    const { data, error: apiError } = await api.PATCH(
      "/admin/clients/{clientId}/webhook-subscriptions/{id}",
      {
        params: { path: { clientId, id: sub.id } },
        body: { active: !sub.active },
      },
    );
    setTogglingId(null);
    if (apiError || !data) {
      setError("Failed to update subscription.");
      return;
    }
    setSubscriptions((subs) => subs.map((s) => (s.id === data.id ? data : s)));
  }

  async function handleRetry(subscriptionId: string, deliveryId: string) {
    setDeliveriesById((prev) => {
      const current = prev[subscriptionId];
      if (!current) return prev;
      return {
        ...prev,
        [subscriptionId]: {
          ...current,
          retrying: { ...current.retrying, [deliveryId]: true },
        },
      };
    });

    const { data, error: apiError } = await api.POST(
      "/admin/clients/{clientId}/webhook-subscriptions/{id}/deliveries/{deliveryId}/retry",
      {
        params: {
          path: { clientId, id: subscriptionId, deliveryId },
        },
      },
    );

    setDeliveriesById((prev) => {
      const current = prev[subscriptionId];
      if (!current) return prev;
      const nextRetrying = { ...current.retrying };
      delete nextRetrying[deliveryId];
      if (apiError || !data) {
        return {
          ...prev,
          [subscriptionId]: {
            ...current,
            retrying: nextRetrying,
            error: "Failed to retry delivery.",
          },
        };
      }
      return {
        ...prev,
        [subscriptionId]: {
          ...current,
          retrying: nextRetrying,
          deliveries: current.deliveries.map((d) =>
            d.id === data.id ? data : d,
          ),
        },
      };
    });
  }

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div>
      <div className="mb-4 flex items-start justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          Webhook subscriptions are created by the Client via{" "}
          <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
            POST /webhook-subscriptions
          </code>{" "}
          using their API key. From here, admins can edit, disable, and inspect deliveries.
        </p>
        {loading && <Spinner className="size-5" />}
      </div>

      <div className="rounded-md border">
        {subscriptions.length === 0 && !loading ? (
          <div className="text-muted-foreground p-8 text-center text-sm">
            No webhook subscriptions found.
          </div>
        ) : (
          <Accordion
            type="multiple"
            value={openIds}
            onValueChange={handleAccordionChange}
          >
            {subscriptions.map((sub) => {
              const eventTypes = (sub.eventTypes as string[]) ?? [];
              const consecutiveFailures = Number(sub.consecutiveFailures ?? 0);
              return (
                <AccordionItem
                  key={sub.id}
                  value={sub.id ?? ""}
                  className="px-4"
                >
                  <AccordionTrigger className="hover:no-underline">
                    <div className="grid w-full grid-cols-12 items-center gap-3 pr-2 text-sm">
                      <div className="col-span-5 truncate font-mono text-xs">
                        {sub.url}
                      </div>
                      <div className="col-span-2">
                        <Badge variant={sub.active ? "default" : "outline"}>
                          {sub.active ? "Active" : "Disabled"}
                        </Badge>
                      </div>
                      <div className="col-span-2 text-muted-foreground tabular-nums">
                        {eventTypes.length} event{eventTypes.length === 1 ? "" : "s"}
                      </div>
                      <div className="col-span-3 text-muted-foreground">
                        {consecutiveFailures > 0 ? (
                          <span className="text-destructive">
                            {consecutiveFailures} consecutive failures
                          </span>
                        ) : (
                          `Created ${formatDateTime(sub.createdAt)}`
                        )}
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <SubscriptionDetailPanel
                      clientId={clientId}
                      subscription={sub}
                      eventTypes={eventTypes}
                      deliveriesState={deliveriesById[sub.id ?? ""]}
                      toggling={togglingId === sub.id}
                      onToggleActive={() => handleToggleActive(sub)}
                      onEdit={() => setEditTarget(sub)}
                      onRetry={(deliveryId) => handleRetry(sub.id!, deliveryId)}
                      onReloadDeliveries={() => loadDeliveries(sub.id!)}
                    />
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </div>

      {editTarget && (
        <EditWebhookSubscriptionDialog
          clientId={clientId}
          subscription={editTarget}
          open={!!editTarget}
          onOpenChange={(open) => {
            if (!open) setEditTarget(null);
          }}
          onUpdated={(updated) => {
            setSubscriptions((subs) =>
              subs.map((s) => (s.id === updated.id ? updated : s)),
            );
            setEditTarget(null);
          }}
        />
      )}
    </div>
  );
}

interface SubscriptionDetailPanelProps {
  clientId: string;
  subscription: WebhookSubscription;
  eventTypes: string[];
  deliveriesState: DeliveriesState | undefined;
  toggling: boolean;
  onToggleActive: () => void;
  onEdit: () => void;
  onRetry: (deliveryId: string) => void;
  onReloadDeliveries: () => void;
}

function SubscriptionDetailPanel({
  subscription,
  eventTypes,
  deliveriesState,
  toggling,
  onToggleActive,
  onEdit,
  onRetry,
  onReloadDeliveries,
}: SubscriptionDetailPanelProps) {
  return (
    <div className="space-y-4 pb-2">
      <div className="rounded-md border bg-muted/30 p-3">
        <div className="mb-3 flex items-start justify-between gap-3">
          <h4 className="text-xs font-bold tracking-wider text-muted-foreground uppercase">
            Subscription
          </h4>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={onEdit}>
              <PencilIcon className="size-4" />
              Edit
            </Button>
            <Button
              size="sm"
              variant={subscription.active ? "outline" : "default"}
              onClick={onToggleActive}
              disabled={toggling}
            >
              {toggling ? (
                <Spinner className="size-4" />
              ) : subscription.active ? (
                <PowerOffIcon className="size-4" />
              ) : (
                <PowerIcon className="size-4" />
              )}
              {subscription.active ? "Disable" : "Enable"}
            </Button>
          </div>
        </div>
        <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
          <DetailField label="ID">
            {subscription.id ? (
              <span className="flex items-center gap-1">
                <Badge variant="secondary" className="font-mono text-xs">
                  {subscription.id}
                </Badge>
                <CopyButton value={subscription.id} />
              </span>
            ) : (
              "—"
            )}
          </DetailField>
          <DetailField label="URL">
            <span className="flex items-center gap-1">
              <span className="break-all font-mono text-xs">{subscription.url}</span>
              <CopyButton value={subscription.url} />
            </span>
          </DetailField>
          <DetailField label="Consecutive failures">
            <span
              className={
                Number(subscription.consecutiveFailures ?? 0) > 0
                  ? "text-destructive tabular-nums"
                  : "tabular-nums"
              }
            >
              {subscription.consecutiveFailures ?? 0}
            </span>
          </DetailField>
          <DetailField label="Created">
            {formatDateTime(subscription.createdAt)}
          </DetailField>
          <DetailField label="Updated">
            {formatDateTime(subscription.updatedAt)}
          </DetailField>
        </dl>
        {eventTypes.length > 0 && (
          <div className="mt-3">
            <div className="mb-1 text-xs text-muted-foreground">Event types</div>
            <div className="flex flex-wrap gap-1">
              {eventTypes.map((et) => (
                <Badge key={et} variant="secondary" className="font-mono text-xs">
                  {et}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-xs font-bold tracking-wider text-muted-foreground uppercase">
            Recent deliveries
          </h4>
          <Button
            size="sm"
            variant="ghost"
            onClick={onReloadDeliveries}
            disabled={deliveriesState?.loading}
          >
            {deliveriesState?.loading ? (
              <Spinner className="size-4" />
            ) : (
              <RefreshCwIcon className="size-4" />
            )}
            Refresh
          </Button>
        </div>
        {!deliveriesState || deliveriesState.loading ? (
          <div className="flex justify-center py-6">
            <Spinner className="size-5" />
          </div>
        ) : deliveriesState.error ? (
          <p className="py-2 text-sm text-destructive">{deliveriesState.error}</p>
        ) : deliveriesState.deliveries.length === 0 ? (
          <p className="text-muted-foreground text-sm">No deliveries yet.</p>
        ) : (
          <div className="space-y-1">
            {deliveriesState.deliveries.map((delivery) => (
              <DeliveryRow
                key={delivery.id}
                delivery={delivery}
                retrying={!!deliveriesState.retrying[delivery.id ?? ""]}
                onRetry={() => delivery.id && onRetry(delivery.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DetailField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words">{children}</dd>
    </div>
  );
}

const deliveryStatusVariant: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  Delivered: "default",
  Pending: "secondary",
  Failed: "destructive",
};

interface DeliveryRowProps {
  delivery: WebhookDelivery;
  retrying: boolean;
  onRetry: () => void;
}

function DeliveryRow({ delivery, retrying, onRetry }: DeliveryRowProps) {
  const [open, setOpen] = useState(false);
  const variant = deliveryStatusVariant[delivery.status] ?? "outline";
  const canRetry = delivery.status !== "Delivered";

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-md border"
    >
      <div className="flex items-center gap-3 px-3 py-2 text-sm">
        <CollapsibleTrigger
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
          title={open ? "Hide payload" : "Show payload"}
        >
          <ChevronRightIcon
            className={cn(
              "size-4 transition-transform",
              open && "rotate-90",
            )}
          />
        </CollapsibleTrigger>
        <Badge variant={variant} className="shrink-0">
          {delivery.status}
        </Badge>
        <span className="flex-1 truncate font-mono text-xs">{delivery.eventType}</span>
        <span className="shrink-0 tabular-nums text-xs text-muted-foreground">
          {delivery.attempts ?? 0} attempt
          {Number(delivery.attempts ?? 0) === 1 ? "" : "s"}
        </span>
        {delivery.lastStatusCode != null && (
          <span className="shrink-0 tabular-nums text-xs text-muted-foreground">
            HTTP {delivery.lastStatusCode}
          </span>
        )}
        <span className="shrink-0 text-xs text-muted-foreground">
          {formatDateTime(delivery.lastAttemptAt ?? delivery.createdAt)}
        </span>
        {canRetry && delivery.id && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2"
            onClick={onRetry}
            disabled={retrying}
            title="Retry delivery"
          >
            {retrying ? (
              <Spinner className="size-3.5" />
            ) : (
              <RefreshCwIcon className="size-3.5" />
            )}
            Retry
          </Button>
        )}
      </div>
      <CollapsibleContent>
        <DeliveryPayload payload={delivery.payload} />
      </CollapsibleContent>
    </Collapsible>
  );
}

function DeliveryPayload({ payload }: { payload: string | undefined }) {
  const formatted = formatJson(payload);
  return (
    <div className="border-t bg-muted/30">
      <div className="flex items-center justify-between px-3 py-1.5">
        <span className="text-xs font-bold tracking-wider text-muted-foreground uppercase">
          Payload
        </span>
        {payload && <CopyButton value={payload} />}
      </div>
      <pre className="overflow-x-auto px-3 pb-3 font-mono text-xs leading-relaxed">
        {formatted}
      </pre>
    </div>
  );
}

function formatJson(raw: string | undefined): string {
  if (!raw) return "—";
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}
